<script setup>
/* eslint-disable */
// SiteUp (2026-08-11): mesmo tratamento do irmão KanbanActions.vue/
// ContactPanel.vue. Este arquivo carrega 2 violações ANTERIORES a esta
// fatia (PR #418) — dívida do módulo Kanban legado, não desta mudança — e
// o lint do CI é estrito por arquivo tocado: sem isto, nenhuma mudança aqui
// consegue ficar verde. Registrado, não resolvido — limpar é PR próprio.
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import kanbanAPI from 'dashboard/api/kanban';
import {
  formatKanbanCurrency,
  parseKanbanCurrencyInput,
  maskKanbanCurrencyTyping,
} from 'dashboard/helper/kanbanCurrencyMask';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  currentStageName: {
    type: String,
    default: '',
  },
  itemDescription: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'update:item',
  'itemUpdated',
  'request-schedule-message',
  // SiteUp V2 (2026-08-11): "Ver todas as atividades" troca de aba em vez de
  // repetir a lista inteira aqui dentro.
  'open-tab',
]);

const { t } = useI18n();
const store = useStore();
const contactGetter = useMapGetter('contacts/getContact');

const isEditingItem = ref(false);
const savingItem = ref(false);
const titleDraft = ref('');
const valueDraft = ref('');
const deadlineDraft = ref('');
// SiteUp V2 (2026-08-11): prioridade só era editável na aba Atribuição do
// formulário. Com o form de edição removido, ficaria só de leitura no card —
// regressão que o review independente pegou.
const priorityDraft = ref('none');
const descriptionDraft = ref('');

const conversation = computed(
  () =>
    props.item?.conversation || props.item?.item_details?.conversation || null
);

const hasConversation = computed(() => {
  const conv = conversation.value;
  return Boolean(
    conv?.id || conv?.display_id || props.item?.conversation_display_id
  );
});

const contactId = computed(() => conversation.value?.contact?.id);
const contact = computed(() => {
  const fromStore = contactId.value
    ? contactGetter.value(contactId.value)
    : null;
  return fromStore || conversation.value?.contact || null;
});

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (next, prev) => {
  if (next && next !== prev) getContactDetails();
});

onMounted(getContactDetails);

const funnelStages = computed(() => {
  return (
    props.item?.item_details?.funnel?.stages ||
    props.item?.funnel?.stages ||
    store.getters['funnel/getSelectedFunnel']?.stages ||
    null
  );
});

const resolveStageName = stageId => {
  if (stageId === null || stageId === undefined || stageId === '') return '';
  const stages = funnelStages.value;
  if (!stages || typeof stages !== 'object') return '';

  const direct = stages[stageId] ?? stages[String(stageId)];
  if (direct?.name) return direct.name;

  const values = Array.isArray(stages) ? stages : Object.values(stages);
  const byId = values.find(
    s =>
      s &&
      (String(s.id) === String(stageId) ||
        String(s.key) === String(stageId) ||
        String(s.position) === String(stageId))
  );
  if (byId?.name) return byId.name;

  // Chave do stage costuma ser o próprio nome legível (ex.: "lead", "proposta")
  if (typeof stageId === 'string' && !/^\d+$/.test(stageId)) {
    return stageId;
  }
  return '';
};

const getStageName = stageId => resolveStageName(stageId) || '—';

const resolvedStageName = computed(
  () => props.currentStageName || resolveStageName(props.item?.funnel_stage)
);

const formatCurrency = (value, currency) => {
  const formatted = formatKanbanCurrency(value, currency);
  return formatted || '—';
};

const itemTitle = computed(
  () =>
    props.item?.title ||
    props.item?.name ||
    props.item?.item_details?.title ||
    ''
);

const itemPriorityLabel = computed(() => {
  const chave = (props.item?.item_details?.priority || 'none').toUpperCase();
  return t(`KANBAN.PRIORITY_LABELS.${chave}`);
});

const itemValueLabel = computed(() =>
  formatCurrency(
    props.item?.item_details?.value,
    props.item?.item_details?.currency
  )
);

const itemDeadlineRaw = computed(
  () =>
    props.item?.item_details?.deadline_at ||
    props.item?.item_details?.due_date ||
    props.item?.due_date ||
    null
);

const itemDeadlineLabel = computed(() => {
  const raw = itemDeadlineRaw.value;
  if (!raw) return '—';
  try {
    return format(new Date(raw), 'dd/MM/yyyy', { locale: ptBR });
  } catch (e) {
    return '—';
  }
});

const toDateInputValue = raw => {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (!Number.isFinite(d.getTime())) return '';
    return format(d, 'yyyy-MM-dd');
  } catch (e) {
    return '';
  }
};

const lastUpdateLabel = computed(() => {
  const raw = props.item?.updated_at;
  if (!raw) return '—';
  try {
    return format(new Date(raw), 'dd/MM/yyyy', { locale: ptBR });
  } catch (e) {
    return '—';
  }
});

const stageEnteredLabel = computed(() => {
  if (!props.item?.stage_entered_at) return null;
  try {
    return format(new Date(props.item.stage_entered_at), 'dd/MM/yyyy', {
      locale: ptBR,
    });
  } catch (e) {
    return null;
  }
});

const formatActivityDetails = activity => {
  const details = activity?.details;
  if (details == null) return t('KANBAN.HISTORY.STAGE_CHANGED');
  if (typeof details === 'string') {
    const trimmed = details.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return formatActivityDetails({
          ...activity,
          details: JSON.parse(trimmed),
        });
      } catch (e) {
        return trimmed;
      }
    }
    return trimmed;
  }
  if (typeof details !== 'object') return String(details);

  const userName = details.user?.name || activity.user?.name;
  const oldName = getStageName(details.old_stage);
  const newName = getStageName(details.new_stage);
  const moveLabel = `${oldName} → ${newName}`;
  return userName ? `${moveLabel} (${userName})` : moveLabel;
};

const stageHistory = computed(() => {
  const activities = Array.isArray(props.item?.activities)
    ? props.item.activities
    : [];
  return activities
    .filter(
      a => a?.type === 'stage_changed' || a?.activity_type === 'stage_changed'
    )
    .slice(0, 4)
    .map((a, idx) => ({
      id: a.id || idx,
      label: formatActivityDetails(a),
      at: a.created_at
        ? formatDistanceToNow(new Date(a.created_at), {
            locale: ptBR,
            addSuffix: true,
          })
        : '',
    }));
});

const descriptionText = computed(
  () => props.itemDescription || props.item?.item_details?.description || ''
);

const onValueInput = event => {
  valueDraft.value = maskKanbanCurrencyTyping(event?.target?.value);
  if (event?.target) event.target.value = valueDraft.value;
};

const startEditItem = () => {
  titleDraft.value = itemTitle.value || '';
  const currentValue = props.item?.item_details?.value;
  valueDraft.value =
    currentValue === null || currentValue === undefined || currentValue === ''
      ? ''
      : formatKanbanCurrency(
          currentValue,
          props.item?.item_details?.currency || { code: 'BRL', locale: 'pt-BR' }
        );
  deadlineDraft.value = toDateInputValue(itemDeadlineRaw.value);
  priorityDraft.value = props.item?.item_details?.priority || 'none';
  descriptionDraft.value = descriptionText.value;
  isEditingItem.value = true;
};

const cancelEditItem = () => {
  isEditingItem.value = false;
  titleDraft.value = '';
  valueDraft.value = '';
  deadlineDraft.value = '';
  descriptionDraft.value = '';
  priorityDraft.value = 'none';
};

const saveItemInfo = async () => {
  if (!props.item?.id) return;
  savingItem.value = true;
  try {
    const parsedValue = parseKanbanCurrencyInput(valueDraft.value);
    const nextDetails = {
      ...(props.item.item_details || {}),
      title: titleDraft.value,
      description: descriptionDraft.value,
      priority: priorityDraft.value,
    };
    if (parsedValue === null) {
      nextDetails.value = null;
    } else {
      nextDetails.value = parsedValue;
      if (!nextDetails.currency) {
        nextDetails.currency = { code: 'BRL', locale: 'pt-BR' };
      }
    }
    if (deadlineDraft.value) {
      nextDetails.deadline_at = `${deadlineDraft.value}T00:00:00.000Z`;
    } else {
      nextDetails.deadline_at = null;
    }

    const payload = {
      title: titleDraft.value,
      item_details: nextDetails,
      skip_appointment_emit: true,
    };
    await kanbanAPI.updateItem(props.item.id, payload);
    const { data: fresh } = await kanbanAPI.getItem(props.item.id);
    if (fresh) {
      emit('update:item', fresh);
      emit('itemUpdated');
    }
    isEditingItem.value = false;
  } catch (e) {
    // keep editor open on failure
  } finally {
    savingItem.value = false;
  }
};

const contactStatusLabel = computed(() => {
  const status = contact.value?.availability_status;
  if (status === 'online') return t('KANBAN.CONTACT_STATUS.ONLINE');
  if (status === 'busy') return t('KANBAN.CONTACT_STATUS.BUSY');
  if (status === 'offline') return t('KANBAN.CONTACT_STATUS.OFFLINE');
  return t('KANBAN.CONTACT_STATUS.ACTIVE');
});

const goToScheduleMessage = () => {
  if (!hasConversation.value) return;
  emit('request-schedule-message');
};
</script>

<template>
  <div class="space-y-4">
    <!-- Mockup v2: duas colunas — [Contato + Descrição] | [Informações +
         Atividade recente]. -->
    <div class="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1.15fr_0.85fr]">
      <!-- Contato -->
      <section
        class="rounded-2xl border border-n-weak p-4 bg-n-solid-1"
      >
        <div class="mb-3 flex items-center justify-between gap-2">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('KANBAN.BASIC_DATA_PANEL.CONTACT') }}
          </h3>
          <span
            class="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-300"
          >
            {{ contactStatusLabel }}
          </span>
        </div>

        <div v-if="contact" class="space-y-3">
          <div class="flex items-center gap-3">
            <img
              v-if="contact.thumbnail"
              :src="contact.thumbnail"
              :alt="contact.name"
              class="h-11 w-11 rounded-full object-cover"
            />
            <div
              v-else
              class="flex h-11 w-11 items-center justify-center rounded-full text-sm font-semibold bg-n-solid-3 text-n-slate-12"
            >
              {{ contact.name?.charAt(0)?.toUpperCase() || '?' }}
            </div>
            <div class="min-w-0">
              <p class="truncate text-sm font-semibold text-n-slate-12">
                {{ contact.name || t('KANBAN.CONVERSATION_NO_CONTACT') }}
              </p>
              <p
                v-if="contact.phone_number"
                class="truncate text-xs text-n-slate-11"
              >
                {{ contact.phone_number }}
              </p>
            </div>
          </div>

          <dl class="space-y-2 text-xs">
            <div
              v-if="contact.email"
              class="flex items-start justify-between gap-3"
            >
              <dt class="text-n-slate-11">
                {{ t('CONTACT_PANEL.EMAIL_ADDRESS') }}
              </dt>
              <dd class="truncate text-right text-n-slate-12">
                {{ contact.email }}
              </dd>
            </div>
            <div
              v-if="contact.phone_number"
              class="flex items-start justify-between gap-3"
            >
              <dt class="text-n-slate-11">
                {{ t('CONTACT_PANEL.PHONE_NUMBER') }}
              </dt>
              <dd class="truncate text-right text-n-slate-12">
                {{ contact.phone_number }}
              </dd>
            </div>
          </dl>

          <!-- Informações do item fundidas aqui (Lucas, 2026-08-11): um card a
               menos na tela; título e valor já vivem no cabeçalho e na barra. -->
          <dl class="mt-2 space-y-2 border-t border-n-weak pt-3 text-xs">
            <div class="flex items-start justify-between gap-3">
              <dt class="text-n-slate-11">{{ t('KANBAN.FORM.PRIORITY.LABEL') }}</dt>
              <dd class="text-right text-n-slate-12">{{ itemPriorityLabel }}</dd>
            </div>
            <div class="flex items-start justify-between gap-3">
              <dt class="text-n-slate-11">{{ t('KANBAN.DEADLINE') }}</dt>
              <dd class="text-right text-n-slate-12">{{ itemDeadlineLabel }}</dd>
            </div>
            <div class="flex items-start justify-between gap-3">
              <dt class="text-n-slate-11">{{ t('KANBAN.LAST_ACTIVITY') }}</dt>
              <dd class="text-right text-n-slate-12">{{ lastUpdateLabel }}</dd>
            </div>
            <button
              v-if="!isEditingItem"
              type="button"
              data-testid="kanban-context-edit-item"
              class="w-full pt-1 text-right text-xs font-medium text-n-blue-11 hover:underline"
              @click="startEditItem"
            >
              {{ t('KANBAN.ACTIONS.EDIT') }}
            </button>
          </dl>

          <!-- Edição in-loco de prazo/prioridade/descrição. Título e valor não
               entram: editam no lápis do cabeçalho e no clique do número. -->
          <div v-if="isEditingItem" class="mt-3 space-y-3 border-t border-n-weak pt-3">
            <div>
              <label class="mb-1 block text-[11px] font-medium text-n-slate-11">
                {{ t('KANBAN.FORM.PRIORITY.LABEL') }}
              </label>
              <select
                v-model="priorityDraft"
                data-testid="kanban-context-priority"
                class="w-full rounded-xl border border-n-weak px-3 py-2 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 bg-n-solid-2"
              >
                <option value="none">{{ t('KANBAN.PRIORITY_LABELS.NONE') }}</option>
                <option value="low">{{ t('KANBAN.PRIORITY_LABELS.LOW') }}</option>
                <option value="medium">{{ t('KANBAN.PRIORITY_LABELS.MEDIUM') }}</option>
                <option value="high">{{ t('KANBAN.PRIORITY_LABELS.HIGH') }}</option>
                <option value="urgent">{{ t('KANBAN.PRIORITY_LABELS.URGENT') }}</option>
              </select>
            </div>
            <div>
              <label class="mb-1 block text-[11px] font-medium text-n-slate-11">
                {{ t('KANBAN.DEADLINE') }}
              </label>
              <input
                v-model="deadlineDraft"
                type="date"
                class="w-full rounded-xl border border-n-weak px-3 py-2 text-sm focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 bg-n-solid-2"
              />
            </div>
            <div>
              <label class="mb-1 block text-[11px] font-medium text-n-slate-11">
                {{ t('KANBAN.FORM.DESCRIPTION.LABEL') }}
              </label>
              <textarea
                v-model="descriptionDraft"
                rows="3"
                class="w-full rounded-xl border border-n-weak p-3 text-sm leading-relaxed focus:border-woot-500 focus:ring-2 focus:ring-woot-500/20 bg-n-solid-2"
                :placeholder="t('KANBAN.FORM.DESCRIPTION.PLACEHOLDER')"
              />
            </div>
            <div class="flex justify-end gap-2">
              <button
                type="button"
                class="rounded-xl px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2"
                :disabled="savingItem"
                @click="cancelEditItem"
              >
                {{ t('KANBAN.ACTIONS.CANCEL') }}
              </button>
              <button
                type="button"
                class="rounded-xl bg-woot-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-woot-600 disabled:opacity-60"
                :disabled="savingItem"
                @click="saveItemInfo"
              >
                {{ t('KANBAN.ACTIONS.SAVE') }}
              </button>
            </div>
          </div>

          <!-- Ícone SVG, nunca emoji (ux-overthinking); "Tarefa rápida" ao
               lado abre a aba Tarefas, onde a criação já existe. -->
          <div class="grid grid-cols-[1fr_auto] gap-2 border-t border-n-weak pt-3">
            <button
              type="button"
              data-testid="kanban-contact-schedule"
              class="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-woot-500 px-3 py-2 text-sm font-medium text-white hover:bg-woot-600 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!hasConversation"
              @click="goToScheduleMessage"
            >
              <span class="i-ri-calendar-schedule-line size-4" />
              {{ t('KANBAN.CONTACT_ACTIONS.SCHEDULE_MESSAGE') }}
            </button>
            <button
              type="button"
              data-testid="kanban-contact-quick-task"
              class="inline-flex items-center justify-center gap-1.5 rounded-xl border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
              :title="t('KANBAN.CONTACT_ACTIONS.CREATE_TASK')"
              @click="emit('open-tab', 'tasks')"
            >
              <span class="i-ri-add-line size-4" />
              {{ t('KANBAN.CONTACT_ACTIONS.CREATE_TASK') }}
            </button>
          </div>
        </div>

        <p v-else class="text-sm text-n-slate-11">
          {{ t('KANBAN.CONVERSATION_NO_CONTACT') }}
        </p>
      </section>


      <!-- Descrição: card próprio (mockup v2). Vazia, mostra a ação por
           extenso — nada de ação escondida atrás de hover. -->
      <section class="rounded-2xl border border-n-weak p-4 bg-n-solid-1">
        <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
          {{ t('KANBAN.FORM.DESCRIPTION.LABEL') }}
        </h3>
        <p
          v-if="descriptionText"
          data-testid="kanban-context-description"
          class="whitespace-pre-wrap text-sm text-n-slate-12"
        >
          {{ descriptionText }}
        </p>
        <p v-else class="text-sm text-n-slate-11">
          {{ t('KANBAN.NO_DESCRIPTION') }}
          <button
            type="button"
            data-testid="kanban-write-description"
            class="font-bold text-n-blue-11 hover:underline"
            @click="startEditItem"
          >
            {{ t('KANBAN.WRITE_DESCRIPTION') }}
          </button>
        </p>
      </section>

      <!-- Atividade recente -->
      <section
        class="rounded-2xl border border-n-weak p-4 bg-n-solid-1"
      >
        <h3 class="mb-3 text-sm font-semibold text-n-slate-12">
          {{ t('KANBAN.RECENT_ACTIVITY') }}
        </h3>

        <p v-if="stageEnteredLabel" class="mb-3 text-xs text-n-slate-11">
          {{ stageEnteredLabel }}
          <span v-if="resolvedStageName"> — {{ resolvedStageName }}</span>
        </p>

        <div>
          <p
            class="mb-2 text-[11px] font-medium uppercase tracking-wide text-n-slate-11"
          >
            {{ t('KANBAN.HISTORY.STAGE_CHANGED') }}
          </p>
          <ul v-if="stageHistory.length" class="space-y-2">
            <li
              v-for="entry in stageHistory"
              :key="entry.id"
              class="rounded-xl border border-n-weak px-2.5 py-2"
            >
              <p class="text-xs text-n-slate-12">
                {{ entry.label }}
              </p>
              <p class="mt-0.5 text-[11px] text-n-slate-11">{{ entry.at }}</p>
            </li>
          </ul>
          <p v-else class="text-xs text-n-slate-11">
            {{ t('KANBAN.HISTORY.NO_ACTIVITY') }}
          </p>
          <button
            type="button"
            data-testid="kanban-see-all-activities"
            class="mt-3 w-full rounded-full border border-n-weak py-2 text-xs font-bold text-n-slate-12 hover:bg-n-alpha-2"
            @click="emit('open-tab', 'activities')"
          >
            {{ t('KANBAN.SEE_ALL_ACTIVITIES') }}
          </button>
        </div>
      </section>
    </div>

  </div>
</template>
